import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { QueryKey } from '@/Config/api/queryKey';
import {
  ConversationExamplesResponseDto,
  KBSourcesSortKey,
  KBSourcesStatsDto,
} from '@/Domain/api/Analytics/dto/KBSources.dto';
import { DateRangeState } from '@/Domain/types/DateRange.types';

const toISODate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

type UseKBSourcesAnalyticsParams = {
  range: DateRangeState;
  pageIndex?: number;
  sortKey?: KBSourcesSortKey;
  sortDesc?: boolean;
};

export const useKBSourcesAnalytics = ({
  range,
  pageIndex = 0,
  sortKey = 'raw_usage_count',
  sortDesc = true,
}: UseKBSourcesAnalyticsParams) => {
  const start = toISODate(range.startDate);
  const end = toISODate(range.endDate);

  const { data, isLoading, isSuccess, error, refetch } = useQuery<KBSourcesStatsDto>({
    queryKey: [QueryKey.analytics.kbSources, { start, end, pageIndex, sortKey, sortDesc }],
    enabled: !!start && !!end,
  });

  const items = data?.items ?? [];
  const totalCount = data?.count ?? 0;
  const totalUsageCount = data?.total_usage_count ?? 0;
  const uniqueArticlesCount = data?.unique_articles_count ?? 0;
  const uniqueConversationsCount = data?.unique_conversations_count ?? 0;
  const kbBackedRepliesPercent = data?.kb_backed_replies_percent ?? 0;
  const topArticleSharePercent = data?.top_article_share_percent ?? 0;
  const currentAutomaticAnalysisSamplingRate =
    data?.current_automatic_analysis_sampling_rate ?? null;
  // The API clamps the window to 30 days but the shared picker offers longer presets, so
  // compare what we asked for with what was aggregated.
  const isWindowClamped = !!data?.start_date && !!start && data.start_date !== start;

  return {
    items,
    totalCount,
    effectiveStartDate: data?.start_date,
    effectiveEndDate: data?.end_date,
    isWindowClamped,
    totalUsageCount,
    uniqueArticlesCount,
    uniqueConversationsCount,
    kbBackedRepliesPercent,
    topArticleSharePercent,
    currentAutomaticAnalysisSamplingRate,
    isLoading,
    isSuccess,
    error,
    refetch,
    pageIndex,
    offset: data?.offset ?? 0,
  };
};

type UseKBSourcesExamplesParams = {
  articleId: number | null;
  range: DateRangeState;
  enabled?: boolean;
};

export const useKBSourcesExamples = ({
  articleId,
  range,
  enabled = true,
}: UseKBSourcesExamplesParams) => {
  const start = toISODate(range.startDate);
  const end = toISODate(range.endDate);

  const { data, isLoading, error } = useQuery<ConversationExamplesResponseDto>({
    queryKey: [QueryKey.analytics.kbSourcesExamples, { articleId: articleId!, start, end }],
    enabled: enabled && !!articleId && !!start && !!end,
  });

  return {
    articleTitle: data?.article_title ?? '',
    examples: data?.examples ?? [],
    isLoading,
    error,
  };
};
